import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useWeather, WeatherBadge } from './WeatherBadge';
import { getTeamStatsUrl } from '../utils/teamLinks';
import { TeamStatsModal } from './TeamStatsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamtix-manager/artifacts/7dt7doo8_TicketASaurus.png';

// Global Calendar Component - Read-only combined view of all events
export const GlobalCalendarView = ({ events, onBack, forceViewMode = null, userEmail = null, userPartnerId = null }) => {
  // Load cached data immediately for instant display
  const [allGames, setAllGames] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedGlobalGames');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [allTicketData, setAllTicketData] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedGlobalTickets');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Show past months toggle - default to false (only show current & future)
  const [showPastMonths, setShowPastMonths] = useState(false);
  // Use forceViewMode if provided, otherwise detect based on screen width (mobile default for tablets)
  const [isMobile, setIsMobile] = useState(() => {
    if (forceViewMode === 'mobile') return true;
    if (forceViewMode === 'desktop') return false;
    return window.innerWidth <= 1366; // Default mobile for tablets too
  });

  // Weather forecast data
  const weatherForecast = useWeather();
  
  // My Events vs All Events toggle
  const [showMyEventsOnly, setShowMyEventsOnly] = useState(false);
  // Desktop: hide past events within the current month (persist across visits)
  const [hidePastEventsDesktop, setHidePastEventsDesktop] = useState(() => {
    return localStorage.getItem('globalCalendarHidePastEvents') === 'true';
  });

  // Team stats modal
  const [teamStatsModal, setTeamStatsModal] = useState(null); // { url, teamName }

  // US Federal Holidays (fixed and calculated)
  const getUSHolidays = (year) => {
    const holidays = {};
    
    // Fixed date holidays
    holidays[`${year}-01-01`] = "New Year's Day";
    holidays[`${year}-07-04`] = "Independence Day";
    holidays[`${year}-11-11`] = "Veterans Day";
    holidays[`${year}-12-25`] = "Christmas Day";
    holidays[`${year}-12-31`] = "New Year's Eve";
    
    // Valentine's Day & St. Patrick's Day
    holidays[`${year}-02-14`] = "Valentine's Day";
    holidays[`${year}-03-17`] = "St. Patrick's Day";
    
    // Halloween
    holidays[`${year}-10-31`] = "Halloween";
    
    // MLK Day - 3rd Monday in January
    const mlkDay = getNthWeekdayOfMonth(year, 0, 1, 3); // January, Monday, 3rd
    holidays[formatDateKey(mlkDay)] = "MLK Day";
    
    // Presidents Day - 3rd Monday in February
    const presDay = getNthWeekdayOfMonth(year, 1, 1, 3); // February, Monday, 3rd
    holidays[formatDateKey(presDay)] = "Presidents Day";
    
    // Memorial Day - Last Monday in May
    const memDay = getLastWeekdayOfMonth(year, 4, 1); // May, Monday
    holidays[formatDateKey(memDay)] = "Memorial Day";
    
    // Labor Day - 1st Monday in September
    const laborDay = getNthWeekdayOfMonth(year, 8, 1, 1); // September, Monday, 1st
    holidays[formatDateKey(laborDay)] = "Labor Day";
    
    // Columbus Day - 2nd Monday in October
    const columbusDay = getNthWeekdayOfMonth(year, 9, 1, 2); // October, Monday, 2nd
    holidays[formatDateKey(columbusDay)] = "Columbus Day";
    
    // Thanksgiving - 4th Thursday in November
    const thanksgiving = getNthWeekdayOfMonth(year, 10, 4, 4); // November, Thursday, 4th
    holidays[formatDateKey(thanksgiving)] = "Thanksgiving";
    
    // Day after Thanksgiving (Black Friday)
    const blackFriday = new Date(thanksgiving);
    blackFriday.setDate(blackFriday.getDate() + 1);
    holidays[formatDateKey(blackFriday)] = "Black Friday";
    
    // Mother's Day - 2nd Sunday in May
    const mothersDay = getNthWeekdayOfMonth(year, 4, 0, 2); // May, Sunday, 2nd
    holidays[formatDateKey(mothersDay)] = "Mother's Day";
    
    // Father's Day - 3rd Sunday in June
    const fathersDay = getNthWeekdayOfMonth(year, 5, 0, 3); // June, Sunday, 3rd
    holidays[formatDateKey(fathersDay)] = "Father's Day";
    
    // Easter (complex calculation)
    const easter = getEasterDate(year);
    holidays[formatDateKey(easter)] = "Easter Sunday";
    
    return holidays;
  };
  
  // Helper: Get nth weekday of month (0=Sun, 1=Mon, etc.)
  const getNthWeekdayOfMonth = (year, month, weekday, n) => {
    const firstDay = new Date(year, month, 1);
    let dayOfWeek = firstDay.getDay();
    let diff = (weekday - dayOfWeek + 7) % 7;
    let day = 1 + diff + (n - 1) * 7;
    return new Date(year, month, day);
  };
  
  // Helper: Get last weekday of month
  const getLastWeekdayOfMonth = (year, month, weekday) => {
    const lastDay = new Date(year, month + 1, 0);
    let dayOfWeek = lastDay.getDay();
    let diff = (dayOfWeek - weekday + 7) % 7;
    return new Date(year, month + 1, -diff);
  };
  
  // Helper: Format date as YYYY-MM-DD
  const formatDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  // Easter calculation (Anonymous Gregorian algorithm)
  const getEasterDate = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month, day);
  };
  
  // Get holidays for relevant years
  const allHolidays = { ...getUSHolidays(2025), ...getUSHolidays(2026) };

  // State for selected game popup
  const [selectedGame, setSelectedGame] = useState(null);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to parse date as local date (not UTC)
  const parseLocalDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      console.warn('Invalid date string:', dateString);
      return new Date(0); // Return epoch date for invalid dates
    }
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      console.warn('Invalid date format:', dateString);
      return new Date(0);
    }
    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.warn('Invalid date numbers:', dateString);
      return new Date(0);
    }
    return new Date(year, month - 1, day);
  };

  // Format time to 12-hour AM/PM format
  const formatTime12h = (timeStr) => {
    if (!timeStr || timeStr === 'TBD') return 'TBD';
    
    // If already has AM/PM, return as is
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
      return timeStr;
    }
    
    // Parse 24-hour format (HH:MM or H:MM)
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }
    
    return timeStr;
  };

  // Get team logo based on sport type
  const getTeamLogo = (sport) => {
    const s = sport?.toLowerCase();
    if (s === 'hockey') return '/dallas-stars-logo.svg';
    if (s === 'football') return '/dallas-cowboys-logo.png';
    if (s === 'basketball') return '/dallas-mavs-logo.png';
    if (s === 'baseball') return '/texas-rangers-logo.png';
    return '/favicon-ticketasaurus.svg';
  };

  // Get local team name for stats modal
  const getLocalTeam = (sport) => {
    const s = sport?.toLowerCase();
    if (s === 'hockey') return 'Dallas Stars';
    if (s === 'football') return 'Dallas Cowboys';
    if (s === 'basketball') return 'Dallas Mavericks';
    if (s === 'baseball') return 'Texas Rangers';
    return null;
  };

  // Get sport color
  const getSportColor = (sport) => {
    const s = sport?.toLowerCase();
    if (s === 'hockey') return '#006847'; // Stars green
    if (s === 'football') return '#003594'; // Cowboys blue
    if (s === 'basketball') return '#0053BC'; // Mavs blue
    if (s === 'baseball') return '#C0111F'; // Rangers red
    return '#666666';
  };

  // Partner colors for ticket allocations
  const partnerColors = {
    'F': 'bg-blue-500',
    'B': 'bg-orange-500', 
    'Y': 'bg-green-500',
    'G': 'bg-purple-500',
    'R': 'bg-red-500'
  };

  // Fetch games, ticket data, and settings for all events using optimized endpoint
  useEffect(() => {
    const fetchAllData = async () => {
      // If we have cached data, show "refreshing" instead of full loading
      const hasCachedData = allGames.length > 0;
      if (hasCachedData) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      
      try {
        // Use optimized single-call endpoint for all events
        const eventIds = events.map(e => e.id).join(',');
        const response = await axios.get(`${API}/events/global-calendar-data?event_ids=${eventIds}${userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`);
        
        if (response.data.success) {
          const { games, tickets } = response.data;
          
          // Add eventId to each game for compatibility with existing code
          const gamesWithEventId = games.map(game => ({
            ...game,
            eventId: game.event_id,
            eventName: game.eventName,
            eventSport: game.eventSport,
            eventColor: game.eventColor || getSportColor(game.eventSport),
            totalSeats: game.totalSeats || 6
          }));
          
          // Sort by date
          gamesWithEventId.sort((a, b) => {
            const dateA = parseLocalDate(a.date);
            const dateB = parseLocalDate(b.date);
            return dateA - dateB;
          });
          
          setAllGames(gamesWithEventId);
          setAllTicketData(tickets);
          
          // Cache the data for instant load next time
          try {
            localStorage.setItem('cachedGlobalGames', JSON.stringify(gamesWithEventId));
            localStorage.setItem('cachedGlobalTickets', JSON.stringify(tickets));
            localStorage.setItem('cachedGlobalTimestamp', Date.now().toString());
          } catch (e) {
            console.warn('Failed to cache data:', e);
          }
        }
      } catch (err) {
        console.error('Error fetching global calendar data:', err);
        // Fallback to old method if new endpoint fails
        try {
          const dataPromises = events.map(async (event) => {
            try {
              const [gamesRes, ticketsRes, settingsRes] = await Promise.all([
                axios.get(`${API}/games/schedule?event_id=${event.id}${(event.sport === 'family' || event.sport === 'concerts') && userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`),
                axios.get(`${API}/tickets/assignments?event_id=${event.id}`),
                axios.get(`${API}/settings/?event_id=${event.id}`)
              ]);
              
              const games = gamesRes.data.games || [];
              const tickets = ticketsRes.data.assignments || ticketsRes.data || {};
              const settings = settingsRes.data.settings || {};
              const totalSeats = settings.total_seats || 6;
              
              const gamesWithEvent = games.map(game => ({
                ...game,
                eventId: event.id,
                eventName: event.teamName,
                eventSport: event.sport,
                eventColor: event.primaryColor || getSportColor(event.sport),
                totalSeats: totalSeats
              }));
              
              return { games: gamesWithEvent, tickets, eventId: event.id };
            } catch (err) {
              return { games: [], tickets: {}, eventId: event.id };
            }
          });
          
          const allData = await Promise.all(dataPromises);
          
          // Helper to parse time to minutes for sorting
          const parseTimeToMinutes = (timeStr) => {
            if (!timeStr) return 9999; // No time = end of day
            const time = timeStr.toLowerCase().trim();
            const match = time.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
            if (!match) return 9999;
            
            let hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2] || '0', 10);
            const period = (match[3] || '').toLowerCase();
            
            // Convert to 24-hour format
            if (period === 'pm' && hours !== 12) hours += 12;
            if (period === 'am' && hours === 12) hours = 0;
            
            return hours * 60 + minutes;
          };
          
          const combinedGames = allData.flatMap(d => d.games).sort((a, b) => {
            const dateA = parseLocalDate(a.date);
            const dateB = parseLocalDate(b.date);
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA - dateB;
            }
            // Same date - sort by time
            const timeA = parseTimeToMinutes(a.timeCT || a.time);
            const timeB = parseTimeToMinutes(b.timeCT || b.time);
            return timeA - timeB;
          });
          
          const combinedTickets = {};
          allData.forEach(d => {
            Object.keys(d.tickets).forEach(key => {
              combinedTickets[`${d.eventId}_${key}`] = d.tickets[key];
            });
          });
          
          setAllGames(combinedGames);
          setAllTicketData(combinedTickets);
          
          // Cache fallback data too
          try {
            localStorage.setItem('cachedGlobalGames', JSON.stringify(combinedGames));
            localStorage.setItem('cachedGlobalTickets', JSON.stringify(combinedTickets));
            localStorage.setItem('cachedGlobalTimestamp', Date.now().toString());
          } catch (e) {
            console.warn('Failed to cache data:', e);
          }
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
        }
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };
    
    if (events.length > 0) {
      fetchAllData();
    }
  }, [events, userEmail]);

  // Get current month key for scrolling
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  // Group all games by month first
  const allGamesByMonth = allGames.reduce((acc, game) => {
    const date = parseLocalDate(game.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(game);
    return acc;
  }, {});

  const allSortedMonths = Object.keys(allGamesByMonth).sort();
  
  // For mobile: Filter games to hide past events by default
  // For desktop: Show all games in current/future months
  const filterGamesForDisplay = (games, monthKey) => {
    if (showPastMonths) return games; // Show all when viewing past
    if (!isMobile) return games; // Desktop shows all games in month
    // Mobile: Filter out past events
    return games.filter(game => game.date >= todayStr);
  };
  
  // Filter to show only current and future months by default
  // When showPastMonths is true, show all months
  const sortedMonths = showPastMonths 
    ? allSortedMonths 
    : allSortedMonths.filter(m => m >= currentMonthKey);
  
  // Use filtered months for display, with filtered games for mobile
  const gamesByMonth = sortedMonths.reduce((acc, monthKey) => {
    const filteredGames = filterGamesForDisplay(allGamesByMonth[monthKey], monthKey);
    if (filteredGames.length > 0) {
      acc[monthKey] = filteredGames;
    }
    return acc;
  }, {});
  
  // Recalculate sorted months based on filtered games (remove empty months)
  const displayMonths = Object.keys(gamesByMonth).sort();

  // Filter for "My Events" — only games where user has tickets/parking or owns family event
  const isMyGame = (game) => {
    if (!userPartnerId && !userEmail) return true;
    const eventSport = (game.eventSport || '').toLowerCase();
    // Family/concert events: check user_email ownership
    if (eventSport === 'family' || eventSport === 'concerts') {
      return game.user_email === userEmail;
    }
    // Sports events: check if user's partner_id is in tickets or parking
    const ticketKey = `${game.eventId}_${game.game_index}`;
    const td = allTicketData[ticketKey] || {};
    const tickets = td.tickets || [];
    const parking = td.parking || [];
    return tickets.includes(userPartnerId) || parking.includes(userPartnerId);
  };

  // Apply "My Events" filter on top of existing month data
  const getDisplayGames = (monthGames) => {
    if (!showMyEventsOnly) return monthGames;
    return monthGames.filter(isMyGame);
  };

  // Count past events for button label (all past games, not just months)
  const pastEventsCount = allGames.filter(game => game.date < todayStr).length;

  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Only show full loading screen if no cached data
  if (loading && allGames.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a1a 50%, #0d0d1f 100%)' }}>
        {/* Epic Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-cyan-400/10 to-transparent transform -skew-x-12 blur-sm"></div>
          <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/35 via-cyan-400/10 to-transparent transform skew-x-12 blur-sm"></div>
          <div className="absolute top-[5%] left-[10%] w-32 h-32 bg-cyan-400/30 rounded-full blur-[60px] animate-pulse"></div>
          <div className="absolute top-[5%] right-[10%] w-32 h-32 bg-purple-500/30 rounded-full blur-[60px] animate-pulse"></div>
          <div className="absolute bottom-0 left-0 right-0 h-48" style={{ background: 'linear-gradient(to top, rgba(34, 211, 238, 0.15) 0%, rgba(139, 92, 246, 0.08) 30%, transparent 100%)' }}></div>
        </div>
        <Card className="w-full max-w-md relative z-10 border-2 border-cyan-400/70" style={{ boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2)' }}>
          <CardContent className="p-6 text-center">
            <div className="text-4xl mb-4">📅</div>
            <p className="text-lg">Loading Global Calendar...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mobile View - List based like existing calendar
  if (isMobile) {
    return (
      <>
      <div className="min-h-screen p-2 relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a1a 50%, #0d0d1f 100%)' }}>
        {/* Epic Background Elements - Static for performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/30 via-cyan-400/5 to-transparent transform -skew-x-12 blur-sm"></div>
          <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-purple-500/25 via-purple-500/5 to-transparent transform skew-x-12 blur-sm"></div>
          <div className="absolute top-[3%] left-[5%] w-24 h-24 bg-cyan-400/20 rounded-full blur-[50px]"></div>
          <div className="absolute top-[3%] right-[5%] w-24 h-24 bg-purple-500/20 rounded-full blur-[50px]"></div>
          <div className="absolute top-1/4 left-0 w-[40%] h-[50%] bg-gradient-to-r from-purple-600/10 via-purple-600/5 to-transparent blur-[80px]"></div>
          <div className="absolute top-1/4 right-0 w-[40%] h-[50%] bg-gradient-to-l from-cyan-500/10 via-cyan-500/5 to-transparent blur-[80px]"></div>
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2322d3ee' fill-opacity='0.4'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
          <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to top, rgba(34, 211, 238, 0.1) 0%, transparent 100%)' }}></div>
        </div>
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-3 px-2 relative z-10">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onBack}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-8 px-2 text-xs"
          >
            ← Back
          </Button>
          <div className="flex items-center gap-1">
            <img src={LOGO_URL} alt="TicketASaurus" className="h-6 w-auto" />
            <span className="text-sm font-bold text-white">Global Calendar</span>
            {isRefreshing && <span className="text-[10px] text-yellow-300 ml-1 animate-pulse">↻</span>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPastMonths(!showPastMonths)}
            className="h-8 px-2 text-xs bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            {showPastMonths ? 'Hide Past' : `View Past (${pastEventsCount})`}
          </Button>
        </div>

        {/* Mobile Legend - Only show non-hidden events */}
        <Card className="mb-2 bg-gray-900 border-gray-700">
          <CardContent className="p-2">
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
              {events.filter(event => !event.is_hidden).map((event, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <img src={getTeamLogo(event.sport)} alt={event.teamName} className="w-4 h-4 object-contain" />
                  <span className="font-medium text-white">{event.teamName.split(' ').pop()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Events / All Events toggle */}
        <div className="flex justify-center mb-3 relative z-10">
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-600">
            <button
              data-testid="toggle-all-events"
              onClick={() => setShowMyEventsOnly(false)}
              className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                !showMyEventsOnly
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              All Events
            </button>
            <button
              data-testid="toggle-my-events"
              onClick={() => setShowMyEventsOnly(true)}
              className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                showMyEventsOnly
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              My Events
            </button>
          </div>
        </div>

        {/* Mobile List View - matches existing calendar structure */}
        <div className="space-y-3">
          {displayMonths.length === 0 ? (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-4 text-center">
                <p className="text-gray-400">No upcoming events found</p>
              </CardContent>
            </Card>
          ) : (
            displayMonths.map((monthKey) => {
              const gamesThisMonth = gamesByMonth[monthKey];
              
              // Sort games within month by date then time
              const sortedGamesThisMonth = [...gamesThisMonth].sort((a, b) => {
                if (a.date !== b.date) return a.date < b.date ? -1 : 1;
                // Same date - sort by time
                const parseT = (t) => {
                  if (!t) return 9999;
                  const m = t.toLowerCase().trim().match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
                  if (!m) return 9999;
                  let h = parseInt(m[1], 10);
                  const min = parseInt(m[2] || '0', 10);
                  const p = (m[3] || '').toLowerCase();
                  if (p === 'pm' && h !== 12) h += 12;
                  if (p === 'am' && h === 12) h = 0;
                  return h * 60 + min;
                };
                return parseT(a.timeCT || a.time) - parseT(b.timeCT || b.time);
              });
              
              // Apply My Events filter
              const filteredGames = getDisplayGames(sortedGamesThisMonth);
              if (filteredGames.length === 0) return null;
              
              return (
                <div key={monthKey} id={`month-${monthKey}`} className="border-2 border-gray-700 rounded-lg overflow-hidden bg-gray-900 shadow-lg">
                  <h3 className="text-lg font-bold py-3 px-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center tracking-wide shadow-md">{getMonthName(monthKey)}</h3>
                  
                  <div className="p-1.5 space-y-1.5">
                    {filteredGames.map((game, idx) => {
                      const date = parseLocalDate(game.date);
                      const isPlayoff = game.season_type === 'playoff' || game.type === 'playoff';
                      const isFamilyEvent = game.eventSport?.toLowerCase() === 'family';
                      const isConcertsEvent = game.eventSport?.toLowerCase() === 'concerts';
                      const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
                      
                      // Get ticket data for this game
                      const ticketKey = `${game.eventId}_${game.game_index}`;
                      const gameData = allTicketData[ticketKey] || {};
                      const hasScore = !isNonSportsEvent && (gameData.played === true || (gameData.mavs_score > 0 && gameData.opponent_score > 0));
                      const isWin = hasScore && gameData.mavs_score > gameData.opponent_score;
                      
                      // Check if any tickets are assigned to contacts
                      const ticketAssignments = gameData.ticket_assignments || {};
                      const parkingAssignments = gameData.parking_assignments || {};
                      const hasAssignments = Object.keys(ticketAssignments).length > 0 || Object.keys(parkingAssignments).length > 0;
                      
                      // Get tickets array using event's seat count
                      const totalSeats = game.totalSeats || 6;
                      const rawTickets = gameData.tickets || [];
                      const tickets = rawTickets.length > 0 ? rawTickets.slice(0, totalSeats) : [];
                      // Pad to totalSeats if needed
                      while (tickets.length < totalSeats) {
                        tickets.push('');
                      }
                      
                      // Base background color
                      const getBgColor = () => {
                        if (game.is_assigned_ticket) return 'bg-yellow-900/30';
                        if (game.is_notified_event) return 'bg-cyan-900/30';
                        if (isFamilyEvent) return 'bg-blue-900/30';
                        if (isConcertsEvent) return 'bg-purple-900/30';
                        if (hasScore) return isWin ? 'bg-green-900/50' : 'bg-red-900/50';
                        if (isPlayoff) return 'bg-amber-900/30';
                        return 'bg-gray-800';
                      };
                      
                      // Border color - yellow if assigned, otherwise based on type
                      const getBorderColor = () => {
                        if (hasAssignments) return 'border-yellow-400';
                        if (isFamilyEvent) return 'border-blue-700';
                        if (isConcertsEvent) return 'border-purple-700';
                        if (hasScore) return isWin ? 'border-green-700' : 'border-red-700';
                        if (isPlayoff) return 'border-amber-700';
                        return 'border-gray-700';
                      };
                      
                      return (
                        <div
                          key={idx}
                          className={`px-2 py-2 flex items-center gap-2 rounded-md border-2 ${getBgColor()} ${getBorderColor()} ${hasAssignments ? 'ring-1 ring-yellow-400/50' : ''}`}
                        >
                          {/* Left: Date - Compact */}
                          <div className="w-12 text-center flex-shrink-0">
                            <div className="text-[10px] font-medium text-gray-400 uppercase leading-tight">
                              {date.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div className="text-lg font-bold text-white leading-tight">{date.getDate()}</div>
                            <div className="text-[10px] text-gray-400 leading-tight">
                              {date.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                          </div>
                          
                          {/* Team Logo / Event Icon */}
                          {isFamilyEvent ? (
                            <span className="w-8 h-8 flex items-center justify-center text-2xl flex-shrink-0">🕐</span>
                          ) : isConcertsEvent ? (
                            <span className="w-8 h-8 flex items-center justify-center text-2xl flex-shrink-0">🎭</span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const localTeam = getLocalTeam(game.eventSport);
                                if (localTeam) {
                                  const espnUrl = getTeamStatsUrl(localTeam, game.eventSport);
                                  setTeamStatsModal({ teamName: localTeam, sport: game.eventSport, espnUrl });
                                }
                              }}
                              className="w-8 h-8 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                              data-testid="local-team-logo"
                            >
                              <img 
                                src={getTeamLogo(game.eventSport)} 
                                alt="" 
                                className="w-8 h-8 object-contain" 
                              />
                            </button>
                          )}
                          
                          {/* Center: Game info */}
                          <div className="flex-1 text-center">
                            {/* Line 1: Opponent/Event + Score */}
                            <div className="flex items-center justify-center gap-1">
                              {isNonSportsEvent ? (
                                <span className="text-xs font-semibold text-white">
                                  {game.opponent}
                                </span>
                              ) : (() => {
                                const statsUrl = getTeamStatsUrl(game.opponent, game.eventSport);
                                return statsUrl ? (
                                  <button
                                    className="text-xs font-semibold text-sky-400 underline underline-offset-2 decoration-sky-400/60 hover:text-sky-300"
                                    onClick={(e) => { e.stopPropagation(); setTeamStatsModal({ teamName: game.opponent, sport: game.eventSport, espnUrl: statsUrl }); }}
                                    data-testid="team-stats-link"
                                  >
                                    vs {game.opponent}
                                  </button>
                                ) : (
                                  <span className="text-xs font-semibold text-white">
                                    vs {game.opponent}
                                  </span>
                                );
                              })()}
                              {game.is_assigned_ticket && (
                                <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-yellow-500/30 text-yellow-300 border border-yellow-500/40">
                                  {game.assigned_seats}🎟
                                </span>
                              )}
                              {game.is_notified_event && (
                                <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-cyan-500/30 text-cyan-300 border border-cyan-500/40">
                                  Invited
                                </span>
                              )}
                              {hasScore && (
                                <span className={`text-xs font-bold ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                                  {isWin ? 'W' : 'L'} {gameData.mavs_score}-{gameData.opponent_score}
                                </span>
                              )}
                            </div>
                            {/* Show end date for multi-day events */}
                            {game.end_date && (
                              <div className="text-[10px] text-teal-400 font-medium">
                                🏖️ thru {parseLocalDate(game.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                            {!hasScore && !game.end_date && (
                              <div className="text-[10px] text-gray-400">{formatTime12h(game.timeCT || game.time)}</div>
                            )}
                            
                            {/* Line 2: Ticket allocations - hide for family events */}
                            {!isFamilyEvent && (
                              <div className="flex items-center justify-center gap-0.5 mt-0.5">
                                {tickets.map((t, i) => {
                                  const bgColor = t ? (partnerColors[t] || 'bg-gray-500') : 'bg-gray-600';
                                  const isAssigned = ticketAssignments[String(i)] != null;
                                  return (
                                    <span 
                                      key={i} 
                                      className={`w-4 h-4 rounded text-center text-[10px] font-bold leading-4 ${bgColor} ${t ? 'text-white' : 'text-gray-400'} ${isAssigned ? 'ring-2 ring-yellow-400' : ''}`}
                                    >
                                      {t || '·'}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {/* Notify contacts for family events */}
                            {isFamilyEvent && (gameData.notify_contacts || []).length > 0 && (
                              <div className="flex items-center justify-center gap-0.5 mt-0.5 flex-wrap">
                                {(gameData.notify_contacts || []).map((nc) => (
                                  <span
                                    key={nc.contact_id}
                                    className="inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-semibold bg-blue-600 text-white"
                                  >
                                    {nc.name.split(' ')[0]}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Show assignee names if any */}
                            {hasAssignments && (
                              <div className="text-[9px] text-yellow-400 font-medium mt-0.5">
                                👥 {Object.values(ticketAssignments).map(a => a?.name).filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join(', ')}
                              </div>
                            )}
                            
                            {game.note && (
                              <div className="text-[10px] text-gray-500 italic truncate mt-0.5">{game.note}</div>
                            )}
                          </div>
                          
                          {/* Weather badge */}
                          <WeatherBadge dateStr={game.date} forecast={weatherForecast} compact={true} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Team Stats Modal - Mobile */}
      {teamStatsModal && (
        <TeamStatsModal
          teamName={teamStatsModal.teamName}
          sport={teamStatsModal.sport}
          espnUrl={teamStatsModal.espnUrl}
          onClose={() => setTeamStatsModal(null)}
        />
      )}
      </>
    );
  }

  // Desktop View - Calendar Grid with enhanced game panels
  return (
    <div className="min-h-screen p-4 relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a1a 50%, #0d0d1f 100%)' }}>
      {/* Epic Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-cyan-400/10 to-transparent transform -skew-x-12 blur-sm"></div>
        <div className="absolute top-0 left-1/3 w-2 h-full bg-gradient-to-b from-purple-500/30 via-purple-500/5 to-transparent transform skew-x-6 blur-md"></div>
        <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/35 via-cyan-400/10 to-transparent transform skew-x-12 blur-sm"></div>
        <div className="absolute top-0 right-1/3 w-2 h-full bg-gradient-to-b from-blue-500/25 via-blue-500/5 to-transparent transform -skew-x-6 blur-md"></div>
        <div className="absolute top-[5%] left-[10%] w-32 h-32 bg-cyan-400/30 rounded-full blur-[60px] animate-pulse" style={{ animationDuration: '2s' }}></div>
        <div className="absolute top-[5%] right-[10%] w-32 h-32 bg-purple-500/30 rounded-full blur-[60px] animate-pulse" style={{ animationDuration: '2.5s' }}></div>
        <div className="absolute top-1/4 left-0 w-[50%] h-[60%] bg-gradient-to-r from-purple-600/15 via-purple-600/5 to-transparent blur-[100px]"></div>
        <div className="absolute top-1/4 right-0 w-[50%] h-[60%] bg-gradient-to-l from-cyan-500/15 via-cyan-500/5 to-transparent blur-[100px]"></div>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2322d3ee' fill-opacity='0.4'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-48" style={{ background: 'linear-gradient(to top, rgba(34, 211, 238, 0.15) 0%, rgba(139, 92, 246, 0.08) 30%, transparent 100%)' }}></div>
        <div className="absolute top-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)' }}></div>
      </div>
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-4 relative z-10">
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            ← Back to Events
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TicketASaurus" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-white">
              Global Calendar
              {isRefreshing && <span className="text-sm text-yellow-300 ml-2 animate-pulse">↻ Refreshing...</span>}
            </h1>
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            {/* My Events / All Events toggle */}
            <div className="inline-flex rounded-lg overflow-hidden border border-white/20">
              <button
                data-testid="desktop-toggle-all-events"
                onClick={() => setShowMyEventsOnly(false)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !showMyEventsOnly
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white/10 text-white/70 hover:text-white'
                }`}
              >
                All Events
              </button>
              <button
                data-testid="desktop-toggle-my-events"
                onClick={() => setShowMyEventsOnly(true)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  showMyEventsOnly
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white/10 text-white/70 hover:text-white'
                }`}
              >
                My Events
              </button>
            </div>
            {/* Hide Past Events toggle */}
            <Button
              variant="outline"
              onClick={() => {
                const nv = !hidePastEventsDesktop;
                setHidePastEventsDesktop(nv);
                localStorage.setItem('globalCalendarHidePastEvents', nv.toString());
              }}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="desktop-toggle-hide-past-events"
              title="Hide past events within visible months"
            >
              {hidePastEventsDesktop ? '👁️ Show Past Events' : '🙈 Hide Past Events'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPastMonths(!showPastMonths)}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              {showPastMonths ? 'Hide Past Months' : `View Past (${allSortedMonths.filter(m => m < currentMonthKey).length} months)`}
            </Button>
          </div>
        </div>
      </div>

      {/* Legend - Only show non-hidden events */}
      <div className="max-w-6xl mx-auto mb-4">
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              {events.filter(event => !event.is_hidden).map((event, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <img src={getTeamLogo(event.sport)} alt={event.teamName} className="w-6 h-6 object-contain" />
                  <span className="font-medium">{event.teamName}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Grid */}
      <div className="max-w-6xl mx-auto space-y-6">
        {sortedMonths.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-gray-500">No upcoming games found</p>
            </CardContent>
          </Card>
        ) : (
          sortedMonths.map((monthKey) => {
            const [year, month] = monthKey.split('-');
            const monthNum = parseInt(month) - 1;
            const daysInMonth = getDaysInMonth(parseInt(year), monthNum);
            const firstDay = getFirstDayOfMonth(parseInt(year), monthNum);
            // Desktop: show games in month from allGamesByMonth
            // Optionally filter to only "My Events" and/or hide past events
            let gamesThisMonth = allGamesByMonth[monthKey];
            if (hidePastEventsDesktop && !showPastMonths) {
              gamesThisMonth = gamesThisMonth.filter(g => {
                // Keep multi-day events that end on/after today
                const endStr = g.end_date || g.date;
                return endStr >= todayStr;
              });
            }
            if (showMyEventsOnly) {
              gamesThisMonth = gamesThisMonth.filter(isMyGame);
            }

            // Create a map of games by day, expanding multi-day events to all days they span
            const gamesByDay = gamesThisMonth.reduce((acc, game) => {
              const startDate = parseLocalDate(game.date);
              const startDay = startDate.getDate();
              
              // Check if this is a multi-day event
              if (game.end_date) {
                const endDate = parseLocalDate(game.end_date);
                
                // Add event to each day it spans (within this month)
                let currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                  // Only add if current date is in this month
                  if (currentDate.getMonth() === monthNum && currentDate.getFullYear() === parseInt(year)) {
                    const currentDay = currentDate.getDate();
                    if (!acc[currentDay]) acc[currentDay] = [];
                    // Mark if this is start, middle, or end of multi-day event
                    const isStart = currentDate.getTime() === startDate.getTime();
                    const isEnd = currentDate.getTime() === endDate.getTime();
                    acc[currentDay].push({
                      ...game,
                      _multiDay: true,
                      _isStart: isStart,
                      _isEnd: isEnd,
                      _isContinuation: !isStart
                    });
                  }
                  currentDate.setDate(currentDate.getDate() + 1);
                }
              } else {
                // Single day event
                if (!acc[startDay]) acc[startDay] = [];
                acc[startDay].push(game);
              }
              return acc;
            }, {});

            // Compute leading days to skip (only when Hide Past Events is on and we're in current month)
            // Skip whole leading weeks whose days are all past-day and empty
            let leadingSkipCells = 0;
            if (hidePastEventsDesktop && !showPastMonths) {
              const totalCells = firstDay + daysInMonth;
              const totalWeeks = Math.ceil(totalCells / 7);
              // Find first week that contains today OR any day with games
              const monthDateStr = (d) => `${year}-${String(monthNum + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
              let firstVisibleWeek = 0;
              outer: for (let w = 0; w < totalWeeks; w++) {
                for (let c = 0; c < 7; c++) {
                  const cellIdx = w * 7 + c;
                  const dayNum = cellIdx - firstDay + 1;
                  if (dayNum < 1 || dayNum > daysInMonth) continue;
                  const dstr = monthDateStr(dayNum);
                  if (dstr >= todayStr) { firstVisibleWeek = w; break outer; }
                  if ((gamesByDay[dayNum] || []).length > 0) { firstVisibleWeek = w; break outer; }
                }
              }
              leadingSkipCells = firstVisibleWeek * 7;
            }

            return (
              <Card key={monthKey} id={`month-${monthKey}`} className="bg-gray-900 border-gray-700">
                <CardContent className="p-4">
                  <h3 className="text-2xl font-bold mb-4 text-center text-white">{getMonthName(monthKey)}</h3>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {/* Day headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center font-bold text-sm p-2 text-gray-400 border-b border-gray-700">
                        {day}
                      </div>
                    ))}
                    
                    {/* Empty cells for days before month starts (skipped if leading weeks hidden) */}
                    {leadingSkipCells < firstDay && Array.from({ length: firstDay - leadingSkipCells }).map((_, i) => (
                      <div key={`empty-${i}`} className="min-h-[140px] p-1 bg-gray-800/50"></div>
                    ))}
                    
                    {/* Days of month */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      // Skip days that fall in the hidden leading weeks
                      if ((firstDay + i) < leadingSkipCells) return null;
                      const rawGamesOnDay = gamesByDay[day] || [];
                      
                      // Sort games by time - helper to convert time to minutes for comparison
                      const parseTimeToMinutes = (timeStr) => {
                        if (!timeStr) return 9999; // No time = end of day
                        const time = timeStr.toLowerCase().trim();
                        const match = time.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
                        if (!match) return 9999;
                        
                        let hours = parseInt(match[1], 10);
                        const minutes = parseInt(match[2] || '0', 10);
                        const period = (match[3] || '').toLowerCase();
                        
                        // Convert to 24-hour format
                        if (period === 'pm' && hours !== 12) hours += 12;
                        if (period === 'am' && hours === 12) hours = 0;
                        
                        return hours * 60 + minutes;
                      };
                      
                      const gamesOnDay = [...rawGamesOnDay].sort((a, b) => {
                        const timeA = parseTimeToMinutes(a.timeCT || a.time);
                        const timeB = parseTimeToMinutes(b.timeCT || b.time);
                        return timeA - timeB;
                      });
                      
                      const hasGames = gamesOnDay.length > 0;
                      
                      // Check for holiday
                      const dateKey = `${year}-${String(monthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const holiday = allHolidays[dateKey];

                      const isToday = dateKey === todayStr;

                      return (
                        <div
                          key={day}
                          className={`min-h-[140px] p-2 rounded-lg ${
                            isToday
                              ? 'border-2 border-cyan-400 bg-cyan-950/40 shadow-[0_0_12px_rgba(34,211,238,0.25)]'
                              : `border border-gray-700 ${hasGames ? 'bg-gray-800' : 'bg-gray-800/50'}`
                          }`}
                        >
                          <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-medium ${
                                isToday
                                  ? 'bg-cyan-400 text-gray-900 w-6 h-6 rounded-full flex items-center justify-center font-bold'
                                  : 'text-gray-300'
                              }`}>{day}</span>
                              <div className="flex items-center gap-1">
                                {weatherForecast[dateKey] && (
                                  <WeatherBadge dateStr={dateKey} forecast={weatherForecast} compact={true} />
                                )}
                                {holiday && (
                                  <span className="text-[9px] font-medium text-amber-400 bg-amber-900/50 px-1.5 py-0.5 rounded truncate max-w-[90px]" title={holiday}>
                                    {holiday}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex-1 space-y-1 overflow-auto">
                              {gamesOnDay.map((game, gIdx) => {
                                // Get ticket data for this game
                                const ticketKey = `${game.eventId}_${game.game_index}`;
                                const gameData = allTicketData[ticketKey] || {};
                                
                                // Check event types
                                const isFamilyEvent = game.eventSport?.toLowerCase() === 'family';
                                const isConcertsEvent = game.eventSport?.toLowerCase() === 'concerts';
                                const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
                                
                                const hasScore = !isNonSportsEvent && (gameData.played === true || (gameData.mavs_score > 0 && gameData.opponent_score > 0));
                                const isWin = hasScore && gameData.mavs_score > gameData.opponent_score;
                                
                                // Get tickets array using event's seat count
                                const totalSeats = game.totalSeats || 6;
                                const rawTickets = gameData.tickets || [];
                                const tickets = rawTickets.length > 0 ? rawTickets.slice(0, totalSeats) : [];
                                while (tickets.length < totalSeats) {
                                  tickets.push('');
                                }
                                
                                // Get parking
                                const totalParking = game.totalParking || 2;
                                const rawParking = gameData.parking || [];
                                const parking = rawParking.length > 0 ? rawParking.slice(0, totalParking) : [];
                                while (parking.length < totalParking) {
                                  parking.push('');
                                }
                                
                                // Check if any tickets are assigned to contacts
                                const ticketAssignments = gameData.ticket_assignments || {};
                                const parkingAssignments = gameData.parking_assignments || {};
                                const hasAssignments = Object.keys(ticketAssignments).length > 0 || Object.keys(parkingAssignments).length > 0;
                                
                                // Check if this is a multi-day event
                                const isMultiDay = game._multiDay;
                                const isMultiDayStart = game._isStart;
                                const isMultiDayEnd = game._isEnd;
                                const isMultiDayContinuation = game._isContinuation;
                                
                                // Multi-day events get special compact styling
                                if (isMultiDay) {
                                  return (
                                    <div
                                      key={gIdx}
                                      onClick={() => setSelectedGame({ ...game, gameData, tickets, parking, hasScore, isWin })}
                                      className={`text-xs p-1.5 cursor-pointer transition-all ${
                                        isMultiDayStart ? 'rounded-l-lg rounded-r-none' : 
                                        isMultiDayEnd ? 'rounded-r-lg rounded-l-none' : 
                                        'rounded-none'
                                      } ${
                                        isMultiDayContinuation 
                                          ? 'bg-teal-700/80 hover:bg-teal-600' 
                                          : 'bg-teal-600/90 hover:bg-teal-500'
                                      }`}
                                      style={{ borderLeft: isMultiDayStart ? `3px solid ${game.eventColor}` : 'none' }}
                                    >
                                      {isMultiDayStart ? (
                                        <div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-sm">🏖️</span>
                                            <span className="font-medium truncate text-white text-[10px]">{game.opponent}</span>
                                          </div>
                                          {isFamilyEvent && (gameData.notify_contacts || []).length > 0 && (
                                            <div className="flex items-center gap-0.5 mt-0.5 flex-wrap">
                                              {(gameData.notify_contacts || []).map((nc) => (
                                                <span
                                                  key={nc.contact_id}
                                                  className="inline-flex items-center px-1 py-0 rounded text-[7px] font-semibold bg-blue-500 text-white"
                                                  title={nc.name}
                                                >
                                                  {nc.name.split(' ')[0]}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : isMultiDayEnd ? (
                                        <div className="text-[9px] text-teal-200 italic">📍 ends</div>
                                      ) : (
                                        <div className="text-[9px] text-teal-200 italic">↔️</div>
                                      )}
                                    </div>
                                  );
                                }
                                
                                return (
                                  <div
                                    key={gIdx}
                                    onClick={() => setSelectedGame({ ...game, gameData, tickets, parking, hasScore, isWin })}
                                    className={`text-xs p-1.5 rounded cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${
                                      isFamilyEvent
                                        ? 'bg-blue-900/70 hover:bg-blue-800'
                                        : isConcertsEvent
                                        ? 'bg-purple-900/70 hover:bg-purple-800'
                                        : hasScore
                                        ? isWin 
                                          ? 'bg-green-900/70 hover:bg-green-800'
                                          : 'bg-red-900/70 hover:bg-red-800'
                                        : 'bg-gray-700 hover:bg-gray-600'
                                    } ${hasAssignments ? 'ring-2 ring-yellow-400' : ''}`}
                                    style={{ 
                                      borderLeft: `3px solid ${game.eventColor}`
                                    }}
                                  >
                                    {/* Normal view */}
                                    <div className="flex items-center gap-1 mb-1">
                                      {isFamilyEvent ? (
                                        <span className="w-4 h-4 flex items-center justify-center text-sm flex-shrink-0">🕐</span>
                                      ) : isConcertsEvent ? (
                                        <span className="w-4 h-4 flex items-center justify-center text-sm flex-shrink-0">🎭</span>
                                      ) : (
                                        <img 
                                          src={getTeamLogo(game.eventSport)} 
                                          alt="" 
                                          className="w-4 h-4 object-contain flex-shrink-0"
                                        />
                                      )}
                                      <span className="font-medium truncate text-white">
                                        {isNonSportsEvent ? game.opponent : `vs ${game.opponent}`}
                                      </span>
                                      {game.is_assigned_ticket && (
                                        <span className="inline-flex items-center px-1 py-0 rounded text-[7px] font-bold bg-yellow-500/30 text-yellow-300 border border-yellow-500/40 flex-shrink-0" title={`${game.assigned_seats} ticket${game.assigned_seats > 1 ? 's' : ''} assigned`}>
                                          {game.assigned_seats}🎟
                                        </span>
                                      )}
                                      {game.is_notified_event && (
                                        <span className="inline-flex items-center px-1 py-0 rounded text-[7px] font-bold bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 flex-shrink-0" title="Invited">
                                          Invited
                                        </span>
                                      )}
                                    </div>
                                    {hasScore ? (
                                      <div className={`text-xs font-bold ${isWin ? 'text-green-400' : 'text-red-400'}`}>
                                        {isWin ? 'W' : 'L'} {gameData.mavs_score}-{gameData.opponent_score}
                                      </div>
                                    ) : (
                                      <div className="text-gray-400">{formatTime12h(game.timeCT || game.time)}</div>
                                    )}
                                    {/* Ticket allocations - hide for family events */}
                                    {!isFamilyEvent && (
                                      <div className="flex items-center gap-0.5 mt-1">
                                        {tickets.map((t, ti) => {
                                          const bgColor = t ? (partnerColors[t] || 'bg-gray-500') : 'bg-gray-600';
                                          const isAssigned = ticketAssignments[String(ti)] != null;
                                          return (
                                            <span 
                                              key={ti} 
                                              className={`w-3 h-3 rounded text-center text-[8px] font-bold leading-3 ${bgColor} ${t ? 'text-white' : 'text-gray-400'} ${isAssigned ? 'ring-1 ring-yellow-400' : ''}`}
                                            >
                                              {t || '·'}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {/* Notify contacts for family events */}
                                    {isFamilyEvent && (gameData.notify_contacts || []).length > 0 && (
                                      <div className="flex items-center gap-0.5 mt-1 flex-wrap">
                                        {(gameData.notify_contacts || []).map((nc) => (
                                          <span
                                            key={nc.contact_id}
                                            className="inline-flex items-center px-1 py-0 rounded text-[7px] font-semibold bg-blue-500 text-white"
                                            title={nc.name}
                                          >
                                            {nc.name.split(' ')[0]}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {/* Show assignee indicator */}
                                    {hasAssignments && (
                                      <div className="text-[8px] text-yellow-400 font-medium mt-0.5 truncate">
                                        👥 {Object.values(ticketAssignments).map(a => a?.name).filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).slice(0, 2).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
      {/* Game Details Modal */}
      {selectedGame && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedGame(null)}
        >
          <div 
            className={`relative max-w-md w-full rounded-xl shadow-2xl overflow-hidden ${
              selectedGame.hasScore
                ? selectedGame.isWin 
                  ? 'bg-green-900'
                  : 'bg-red-900'
                : 'bg-gray-800'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{ borderLeft: `6px solid ${selectedGame.eventColor}` }}
          >
            {/* Close button */}
            <button 
              onClick={() => setSelectedGame(null)}
              className="absolute top-3 right-3 text-white/70 hover:text-white text-2xl font-bold z-10"
            >
              ×
            </button>
            
            {/* Header */}
            <div className="p-5 pb-4">
              <div className="flex items-center gap-3 mb-3">
                {selectedGame.eventSport?.toLowerCase() === 'family' ? (
                  <span className="w-10 h-10 flex items-center justify-center text-3xl">🕐</span>
                ) : (
                  <img 
                    src={getTeamLogo(selectedGame.eventSport)} 
                    alt="" 
                    className="w-10 h-10 object-contain"
                  />
                )}
                <div>
                  <div className="text-white text-xl font-bold">
                    {selectedGame.eventSport?.toLowerCase() === 'family' || selectedGame.eventSport?.toLowerCase() === 'concerts' 
                      ? selectedGame.opponent 
                      : (() => {
                        const statsUrl = getTeamStatsUrl(selectedGame.opponent, selectedGame.eventSport);
                        return statsUrl ? (
                          <button
                            className="text-sky-400 underline underline-offset-2 decoration-sky-400/60 hover:text-sky-300"
                            onClick={(e) => { e.stopPropagation(); setTeamStatsModal({ teamName: selectedGame.opponent, sport: selectedGame.eventSport, espnUrl: statsUrl }); }}
                            data-testid="team-stats-link-detail"
                          >
                            vs {selectedGame.opponent}
                          </button>
                        ) : `vs ${selectedGame.opponent}`;
                      })()}
                  </div>
                  <div className="text-gray-300 text-sm">{selectedGame.eventName?.split('(')[0]?.trim() || 'Event'}</div>
                </div>
              </div>
              
              {/* Date & Time / Score */}
              <div className="flex items-center gap-4 mb-4">
                <div className="text-gray-300">
                  <span className="text-lg">📅 {selectedGame.date}</span>
                </div>
                {selectedGame.eventSport?.toLowerCase() !== 'family' && selectedGame.eventSport?.toLowerCase() !== 'concerts' && selectedGame.hasScore ? (
                  <div className={`text-xl font-bold px-3 py-1 rounded ${selectedGame.isWin ? 'bg-green-700 text-green-200' : 'bg-red-700 text-red-200'}`}>
                    {selectedGame.isWin ? '✓ WIN' : '✗ LOSS'} {selectedGame.gameData.mavs_score}-{selectedGame.gameData.opponent_score}
                  </div>
                ) : (
                  <div className="text-gray-300 text-lg">⏰ {formatTime12h(selectedGame.timeCT || selectedGame.time)}</div>
                )}
              </div>
            </div>
            
            {/* Tickets & Parking - Hide for family events, show for concerts */}
            <div className="bg-black/20 p-5">
              {/* Notes - if any */}
              {selectedGame.gameData?.notes && (
                <div className="mb-4 p-3 bg-yellow-900/50 rounded-lg border border-yellow-700/50">
                  <div className="text-yellow-400 text-sm mb-1 font-medium">📝 Notes</div>
                  <div className="text-yellow-100 text-sm">{selectedGame.gameData.notes}</div>
                </div>
              )}
              
              {/* Location for concerts */}
              {selectedGame.eventSport?.toLowerCase() === 'concerts' && selectedGame.gameData?.location && (
                <div className="mb-4 p-3 bg-purple-900/50 rounded-lg border border-purple-700/50">
                  <div className="text-purple-400 text-sm mb-1 font-medium">📍 Location</div>
                  <div className="text-purple-100 text-sm">{selectedGame.gameData.location}</div>
                </div>
              )}
              
              {/* Tickets - hide for family events */}
              {selectedGame.eventSport?.toLowerCase() !== 'family' && (
                <div className="mb-4">
                  <div className="text-gray-400 text-sm mb-2 font-medium">🎟️ Ticket Allocations</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedGame.tickets.map((t, ti) => {
                      const bgColor = t ? (partnerColors[t] || 'bg-gray-500') : 'bg-gray-600';
                      return (
                        <span 
                          key={ti} 
                          className={`w-10 h-10 rounded-lg text-center text-lg font-bold leading-10 ${bgColor} ${t ? 'text-white' : 'text-gray-500'}`}
                        >
                          {t || '·'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Parking - hide for family events */}
              {selectedGame.eventSport?.toLowerCase() !== 'family' && (
                <div>
                  <div className="text-gray-400 text-sm mb-2 font-medium">🅿️ Parking Allocations</div>
                  <div className="flex items-center gap-2">
                    {selectedGame.parking.map((p, pi) => {
                      const bgColor = p ? (partnerColors[p] || 'bg-gray-500') : 'bg-gray-600';
                      return (
                        <span 
                          key={pi} 
                          className={`w-10 h-10 rounded-lg text-center text-lg font-bold leading-10 ${bgColor} ${p ? 'text-white' : 'text-gray-500'}`}
                        >
                          {p || '·'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Notify contacts for family events */}
              {selectedGame.eventSport?.toLowerCase() === 'family' && (
                <div>
                  <div className="text-gray-400 text-sm mb-2 font-medium">👥 Notification Contacts</div>
                  {(selectedGame.gameData?.notify_contacts || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedGame.gameData.notify_contacts || []).map((nc) => (
                        <div
                          key={nc.contact_id}
                          className="flex items-center gap-3 p-2 rounded-lg bg-blue-900/40 border border-blue-700/40"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {nc.name.charAt(0)}
                          </div>
                          <div>
                            <div className="text-white text-sm font-medium">{nc.name}</div>
                            {nc.email && <div className="text-blue-300 text-xs">{nc.email}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-3 text-sm italic">
                      No contacts assigned
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="bg-black/30 px-5 py-3 text-center">
              <span className="text-gray-400 text-xs">Click outside to close</span>
            </div>
          </div>
        </div>
      )}

      {/* Team Stats Modal */}
      {teamStatsModal && (
        <TeamStatsModal
          teamName={teamStatsModal.teamName}
          sport={teamStatsModal.sport}
          espnUrl={teamStatsModal.espnUrl}
          onClose={() => setTeamStatsModal(null)}
        />
      )}
    </div>
  );
};
